import { ProductFlowDataCollection } from '../product-flow/product-flow.types';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { RootReducer, Store } from '@app/app.reducers';
import { WorkflowSelectorData } from '@app/checkout/modules/checkout-shared/reducers/checkout.reducer';
import {
	getStepData,
	ProductFlowPage,
	setStepStateValid,
	Workflow,
} from '@app/product/reducers/product-flow.reducer';
import { ProductEditorService } from '@app/product/services/product-editor.service';
import { TranslatedService } from '@app/shared/services/translated.service';
import { castArray } from '@app/shared/utils/util';
import { faPaintbrush as faPaintbrushRegular } from '@fortawesome/pro-regular-svg-icons/faPaintbrush';
import { faPalette as faPaletteRegular } from '@fortawesome/pro-regular-svg-icons/faPalette';
import { faStar as faStarRegular } from '@fortawesome/pro-regular-svg-icons/faStar';
import { faWandMagic as faMagicRegular } from '@fortawesome/pro-regular-svg-icons/faWandMagic';
import { faPaintbrush } from '@fortawesome/pro-solid-svg-icons/faPaintbrush';
import { faPalette } from '@fortawesome/pro-solid-svg-icons/faPalette';
import { faStar } from '@fortawesome/pro-solid-svg-icons/faStar';
import { faWandMagic } from '@fortawesome/pro-solid-svg-icons/faWandMagic';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

const deliveryMethodToWorkflowMap = new Map<string, ProductFlowPage>([
	['designer', ProductFlowPage.ProductDesigner],
	['upload', ProductFlowPage.Upload],
	['template', ProductFlowPage.SelectTemplate],
	['form', ProductFlowPage.DesignService],
]);

export const WORKFLOW_POOL: Workflow[] = [
	{
		key: ProductFlowPage.ProductDesigner,
		steps: [ProductFlowPage.ProductDesigner, ProductFlowPage.ValidateDesign],
		icon: faPaintbrushRegular,
		iconActive: faPaintbrush,
		title: 'productWorkflowSelect.productDesigner.title',
		description: 'productWorkflowSelect.productDesigner.description',
		showDescription: false,
	},
	{
		key: ProductFlowPage.SelectTemplate,
		steps: [
			ProductFlowPage.SelectTemplate,
			ProductFlowPage.ProductDesigner,
			ProductFlowPage.ValidateDesign,
		],
		icon: faPaletteRegular,
		iconActive: faPalette,
		title: 'productWorkflowSelect.template.title',
		description: 'productWorkflowSelect.template.description',
		showDescription: false,
	},
	{
		key: ProductFlowPage.Upload,
		steps: [ProductFlowPage.Upload],
		icon: faStarRegular,
		iconActive: faStar,
		title: 'productWorkflowSelect.upload.title',
		description: 'productWorkflowSelect.upload.description',
		showDescription: false,
	},
	{
		key: ProductFlowPage.DesignService,
		steps: [ProductFlowPage.DesignService],
		icon: faMagicRegular,
		iconActive: faWandMagic,
		title: 'productWorkflowSelect.designService.title',
		description: 'productWorkflowSelect.designService.description',
		showDescription: false,
	},
];

@UntilDestroy()
@Component({
	selector: 'g-workflow-selector',
	templateUrl: './workflow-selector.component.html',
	styleUrls: ['./workflow-selector.component.scss'],
})
export class WorkflowSelectorComponent implements OnInit, ProductFlowDataCollection {
	@Input() readonly product: api.ProductDto;
	@Output() readonly onSelect = new EventEmitter<Workflow>();
	selectedWorkflow: Workflow;
	keys = ProductFlowPage;
	workflows: Workflow[] = [];

	constructor(
		private readonly store: Store<RootReducer.State>,
		private readonly productEditor: ProductEditorService,
		public translated: TranslatedService,
	) {}

	ngOnInit(): void {
		this.filterWorkflows();

		this.store
			.select(getStepData(this.productEditor.itemId, ProductFlowPage.Workflow))
			.pipe(untilDestroyed(this))
			.subscribe((data) => {
				// Restore selected workflow from product editor service data or reducer state
				const key = this.productEditor.productFlowData?.selectedWorkflow?.key ?? data?.key;
				if (!key) {
					this.setStateValid(false);
					return;
				}
				const selectedWorkflow = this.workflows?.find((workflow) => workflow.key === key);
				// Timeout to prevent ExpressionChangedAfterItHasBeenCheckedError
				setTimeout(() => this.onSelectWorkflow(selectedWorkflow));
			});
	}

	collectStepData(): WorkflowSelectorData {
		if (!this.selectedWorkflow) return;
		return {
			key: this.selectedWorkflow.key,
			steps: this.selectedWorkflow.steps,
		};
	}

	onSelectWorkflow(workflow: Workflow, event?: Event): void {
		if (event) event.stopPropagation();
		this.selectedWorkflow = workflow;
		this.onSelect.emit(workflow);
		this.setStateValid(!!this.selectedWorkflow);
	}

	onToggleDescription(workflow: Workflow, event: Event): void {
		event.stopPropagation();
		for (const wf of this.workflows) {
			if (wf !== workflow) {
				wf.showDescription = false;
			}
		}
		workflow.showDescription = !workflow.showDescription;
	}

	private setStateValid(isValid: boolean) {
		this.store.dispatch(setStepStateValid(isValid));
	}

	private filterWorkflows() {
		const workflowKeys = castArray(this.product?.metadata?.material_delivery_methods ?? [])
			.filter(Boolean)
			.map((dm) => deliveryMethodToWorkflowMap.get(dm));
		this.workflows =
			workflowKeys.length > 0
				? WORKFLOW_POOL.filter((workflow) => workflowKeys.includes(workflow.key))
				: [...WORKFLOW_POOL];
	}
}
